"use client";

import { useEffect, useMemo, useState } from "react";
import dynamic from "next/dynamic";
import Link from "next/link";
import Image from "next/image";
import { confirmCrewDestination, joinCrew, leaveCrew } from "@/app/actions/crews";
import { trackEvent } from "@/lib/analytics";
import { findLabelById, ROLES_TREE, EXPERIENCE_LEVELS } from "@/lib/taxonomy";
import type { CrewRow, CrewMemberRow, ChildCrewCount, LinkedPair } from "./page";
import type { CrewAudienceSummary } from "./client-props";
import type { ChatInitialData } from "./chat/fetch-initial";
import { CrewConditionsFeed, type FeedEntry } from "@/app/_components/conditions/CrewConditionsFeed";

// Chat is shown only to confirmed members on the non-default "Seasonaires" tab,
// so code-split it (and its lucide-react icons) out of the initial bundle.
const CrewChatView = dynamic(
  () => import("./chat/CrewChatView").then((m) => m.CrewChatView),
  {
    ssr: false,
    loading: () => (
      <div className="text-[13px] text-text-muted">Loading chat...</div>
    ),
  }
);

// ─── Types ────────────────────────────────────────────────────────────────────

type Props = {
  crew: CrewRow;
  members: CrewMemberRow[];
  audienceSummary: CrewAudienceSummary;
  childCounts: ChildCrewCount[];
  linkedPairs: LinkedPair[];
  currentUserId?: string | null;
  // "left" and "removed" users are absent from data.members; they fall through to null.
  memberStatus?: "confirmed" | "discovery" | null;
  chatInitial?: ChatInitialData | null;
  conditionsFeed?: FeedEntry[];
};

type CrewTab = "conditions" | "seasonaires";

// ─── Helpers ─────────────────────────────────────────────────────────────────

function getInitials(first: string, last: string) {
  return `${first.charAt(0)}${last.charAt(0)}`.toUpperCase();
}

const AVATAR_GRADIENTS = [
  "from-navy to-navy-mid",
  "from-terracotta to-gold",
  "from-sage to-[#5A7A5F]",
  "from-[#6A5ACD] to-[#8B78DB]",
  "from-[#2E86AB] to-[#45A0C4]",
  "from-navy to-sage",
];

function avatarGradient(userId: string) {
  let hash = 0;
  for (let i = 0; i < userId.length; i++) {
    hash = (hash * 31 + userId.charCodeAt(i)) & 0xffffffff;
  }
  return AVATAR_GRADIENTS[Math.abs(hash) % AVATAR_GRADIENTS.length];
}

function experienceLabel(value: string | null): string {
  if (!value) return "Unknown";
  return EXPERIENCE_LEVELS.find((e) => e.value === value)?.label ?? value;
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  });
}

function isSupabaseUrl(url: string): boolean {
  return url.includes(".supabase.co/storage/");
}

// ─── Main component ──────────────────────────────────────────────────────────

export function CrewPageView({
  crew,
  members,
  audienceSummary,
  childCounts,
  linkedPairs,
  currentUserId,
  memberStatus,
  chatInitial,
  conditionsFeed = [],
}: Props) {
  // Public proof only counts real confirmed members. Discovery rows remain
  // visible to confirmed members inside their own crew experience.
  const confirmedMembers = useMemo(
    () => members.filter((m) => m.status === "confirmed" && !m.profile?.is_sample),
    [members]
  );
  const discoveryMembers = useMemo(
    () => members.filter((m) => m.status === "discovery"),
    [members]
  );

  const [activeTab, setActiveTab] = useState<CrewTab>("conditions");

  // PostHog client-side view event
  useEffect(() => {
    trackEvent("crew_page_viewed", {
      crew_slug: crew.slug,
      crew_kind: crew.kind,
      total_members: audienceSummary.total,
      confirmed_count: audienceSummary.confirmed,
    });
  }, [crew.slug, crew.kind, audienceSummary.total, audienceSummary.confirmed]);

  return (
    <>
      <JsonLd
        crew={crew}
        audienceSummary={audienceSummary}
        childCounts={childCounts}
      />

      {/* ── Hero ─────────────────────────────────────────────────────────── */}
      <section className="bg-navy text-white pt-10 md:pt-16 pb-14 md:pb-20 px-5 md:px-12">
        <div className="max-w-[900px] mx-auto">
          <nav
            aria-label="Breadcrumb"
            className="text-[12px] opacity-70 flex items-center gap-2 mb-5 flex-wrap"
          >
            <Link
              href="/community/crew"
              className="text-white no-underline opacity-80 hover:opacity-100"
            >
              Crews
            </Link>
            {crew.kind === "resort" && (
              <>
                <span aria-hidden>/</span>
                <span className="opacity-70">{crew.season_type === "winter" ? "Snow" : crew.season_type === "summer" ? "Sun" : "Festivals"}</span>
              </>
            )}
            {crew.kind === "regional" && (
              <>
                <span aria-hidden>/</span>
                <span className="opacity-70">Regional</span>
              </>
            )}
            {crew.kind === "festival" && (
              <>
                <span aria-hidden>/</span>
                <span className="opacity-70">Festivals</span>
              </>
            )}
            <span aria-hidden>/</span>
            <span>{crew.destination_name}</span>
          </nav>

          <h1 className="font-serif text-[34px] md:text-[58px] font-bold leading-[1.05] tracking-[-0.015em] mb-5">
            {crew.destination_name} {crew.season_label} Crew
          </h1>

          <p className="text-[15px] md:text-[17px] opacity-80 leading-[1.6] max-w-[600px]">
            {audienceSummary.total === 0 ? (
              <>Be one of the first. No one in the crew yet.</>
            ) : audienceSummary.confirmed === 0 ? (
              <>{audienceSummary.total} seasonaire{audienceSummary.total !== 1 ? "s" : ""} in the crew so far. Be the first to confirm.</>
            ) : (
              <>
                {audienceSummary.confirmed} confirmed and heading there
                {audienceSummary.discovery > 0
                  ? `, ${audienceSummary.discovery} still looking`
                  : ""}
                . {audienceSummary.total} in the crew total.
              </>
            )}
          </p>

          <div className="mt-4 text-[13px] opacity-55">
            {formatDate(crew.date_from)} to {formatDate(crew.date_to)}
          </div>
        </div>
      </section>

      {/* ── Stats strip ──────────────────────────────────────────────────── */}
      <StatsStrip
        audienceSummary={audienceSummary}
      />

      {/* ── Sticky CTA band ──────────────────────────────────────────────── */}
      <CtaBand crew={crew} currentUserId={currentUserId} memberStatus={memberStatus} />

      {/* ── Tab toggle ──────────────────────────────────────────────────── */}
      <div className="max-w-[900px] mx-auto px-5 md:px-12 pt-8 md:pt-12">
        <div className="inline-flex bg-cream rounded-full p-1 mb-5">
          <button
            type="button"
            onClick={() => setActiveTab("conditions")}
            className={`px-5 py-2 rounded-full text-[13px] font-semibold transition-all ${activeTab === "conditions" ? "bg-navy text-white" : "text-navy/60 hover:text-navy"}`}
          >
            Conditions
          </button>
          {memberStatus === "confirmed" && (
            <button
              type="button"
              onClick={() => setActiveTab("seasonaires")}
              className={`px-5 py-2 rounded-full text-[13px] font-semibold transition-all ${activeTab === "seasonaires" ? "bg-navy text-white" : "text-navy/60 hover:text-navy"}`}
            >
              Seasonaires
            </button>
          )}
        </div>
      </div>

      {/* ── Conditions tab ──────────────────────────────────────────────── */}
      {activeTab === "conditions" && (
        <section className="max-w-[900px] mx-auto px-5 md:px-12 pb-12 md:pb-16">
          <CrewConditionsFeed entries={conditionsFeed} />
        </section>
      )}

      {/* ── Seasonaires tab ─────────────────────────────────────────────── */}
      {activeTab === "seasonaires" && memberStatus === "confirmed" && (
        <>
          {/* ── Confirmed members ──────────────────────────────────────── */}
          {confirmedMembers.length > 0 && (
            <section className="max-w-[900px] mx-auto px-5 md:px-12 pb-6">
              <h2 className="font-serif text-[24px] md:text-[30px] font-bold text-navy mb-6 tracking-[-0.01em]">
                Confirmed: heading there
              </h2>
              <MemberGrid
                members={confirmedMembers}
                showConfirmedBadge
                crewDestinationName={crew.destination_name}
                linkedPairs={linkedPairs}
              />
            </section>
          )}

          {/* ── Discovery members ──────────────────────────────────────── */}
          {discoveryMembers.length > 0 && (
            <section className="max-w-[900px] mx-auto px-5 md:px-12 pt-12 md:pt-16 pb-6">
              <h2 className="font-serif text-[24px] md:text-[30px] font-bold text-navy mb-6 tracking-[-0.01em]">
                Also interested in {crew.destination_name}
              </h2>
              <MemberGrid
                members={discoveryMembers}
                showConfirmedBadge={false}
                crewDestinationName={crew.destination_name}
                linkedPairs={linkedPairs}
              />
            </section>
          )}

          {/* ── Destinations in region (regional crews only) ──────────── */}
          {crew.kind === "regional" && childCounts.length > 0 && (
            <section className="max-w-[900px] mx-auto px-5 md:px-12 pt-12 md:pt-16 pb-6">
              <h2 className="font-serif text-[24px] md:text-[30px] font-bold text-navy mb-6 tracking-[-0.01em]">
                Destinations in {crew.destination_name}
              </h2>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {childCounts.map((c) => (
                  <li key={c.slug}>
                    <Link
                      href={`/community/crew/${c.slug}`}
                      className="block border border-border-light rounded-[14px] p-5 hover:border-gold/40 hover:-translate-y-0.5 transition-all no-underline bg-white"
                    >
                      <div className="font-semibold text-[15px] text-charcoal mb-1">
                        {c.destination_name}
                      </div>
                      <div className="text-[13px] text-text-muted">
                        {c.total} in the crew
                        {c.confirmed > 0 && <> · {c.confirmed} confirmed</>}
                      </div>
                    </Link>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {/* ── Crew chat ──────────────────────────────────────────────── */}
          {chatInitial && currentUserId && (
            <section className="max-w-[900px] mx-auto px-5 md:px-12 pb-16 md:pb-20">
              <div className="mt-8 md:grid md:grid-cols-[1fr_380px] md:gap-6">
                {/* Left column: empty spacer on desktop so chat sits on the right */}
                <div />
                <div>
                  <CrewChatView
                    crewId={crew.id}
                    crewSlug={crew.slug}
                    crewName={`${crew.destination_name} ${crew.season_label}`}
                    currentUserId={currentUserId}
                    confirmedMembers={chatInitial.confirmedMembers}
                    initialMessages={chatInitial.messages}
                    initialReactions={chatInitial.reactions}
                    initialMentions={chatInitial.mentions}
                    lastReadAt={chatInitial.lastReadAt}
                  />
                </div>
              </div>
            </section>
          )}
        </>
      )}

      {/* ── Work-at CTA ──────────────────────────────────────────────────── */}
      <section className="max-w-[900px] mx-auto px-5 md:px-12 pt-12 md:pt-16 pb-16 md:pb-20">
        <div className="bg-cream rounded-[18px] p-7 md:p-10">
          <h2 className="font-serif text-[22px] md:text-[28px] font-bold text-navy mb-3">
            Work at {crew.destination_name}
          </h2>
          <p className="text-[14px] md:text-[15px] text-text-body leading-[1.6] mb-6">
            Browse seasonal jobs and employer listings hiring for {crew.destination_name}.
          </p>
          <Link
            href={`/jobs?location=${encodeURIComponent(crew.location_id ?? crew.festival_slug ?? "")}`}
            className="inline-block px-6 py-3 rounded-[10px] bg-navy text-white text-[14px] font-semibold min-h-[44px] leading-[44px] py-0 hover:bg-navy-hover transition-colors no-underline"
          >
            Browse jobs
          </Link>
        </div>
      </section>

      {memberStatus === "discovery" && (
        <section className="max-w-[900px] mx-auto px-5 md:px-12 pb-16 md:pb-20">
          <div className="mt-8 p-6 bg-warm-bg rounded-[16px] border border-border-light text-center">
            <h3 className="font-serif text-lg mb-2">
              Confirm you&apos;re going to unlock the chat
            </h3>
            <p className="text-[13px] text-text-muted">
              Only confirmed crew members can see and post in the chat.
            </p>
          </div>
        </section>
      )}
    </>
  );
}

// ─── Stats strip ──────────────────────────────────────────────────────────────

function StatsStrip({
  audienceSummary,
}: {
  audienceSummary: CrewAudienceSummary;
}) {
  if (audienceSummary.total === 0) return null;

  const topRoles = audienceSummary.roleCounts.slice(0, 3);
  const hasExpData = audienceSummary.experienceCounts.length > 0;
  const hasRoleData = topRoles.length > 0;

  if (!hasExpData && !hasRoleData) return null;

  return (
    <div className="border-b border-border-light bg-warm-bg">
      <div className="max-w-[900px] mx-auto px-5 md:px-12 py-4 flex flex-wrap gap-3">
        {hasExpData && (
          <div className="flex flex-wrap gap-2 items-center">
            <span className="text-[11px] font-semibold uppercase tracking-[1px] text-text-muted mr-1">
              Experience
            </span>
            {audienceSummary.experienceCounts.map(({ value: level, count }) => (
              <span
                key={level}
                className="px-2.5 py-1 bg-white rounded-full text-[12px] text-charcoal border border-border-light"
              >
                {experienceLabel(level)} ({count})
              </span>
            ))}
          </div>
        )}
        {hasRoleData && (
          <div className="flex flex-wrap gap-2 items-center">
            <span className="text-[11px] font-semibold uppercase tracking-[1px] text-text-muted mr-1">
              Top roles
            </span>
            {topRoles.map(({ value: roleId, count }) => (
              <span
                key={roleId}
                className="px-2.5 py-1 bg-white rounded-full text-[12px] text-charcoal border border-border-light"
              >
                {findLabelById(ROLES_TREE, roleId) ?? roleId} ({count})
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── CTA band ─────────────────────────────────────────────────────────────────

function CtaBand({
  crew,
  currentUserId,
  memberStatus,
}: {
  crew: CrewRow;
  currentUserId?: string | null;
  memberStatus?: "confirmed" | "discovery" | null;
}) {
  const [pending, setPending] = useState(false);
  const [done, setDone] = useState(false);
  const [left, setLeft] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // Regional crews: prompt to pick a specific destination
  if (crew.kind === "regional") {
    return (
      <div className="border-b border-border-light bg-white">
        <div className="max-w-[900px] mx-auto px-5 md:px-12 py-5">
          <p className="text-[14px] text-text-body">
            Pick a specific{" "}
            {crew.season_type === "festivals" ? "festival" : "resort"} below to
            confirm and join its crew.
          </p>
        </div>
      </div>
    );
  }

  if (done) {
    return (
      <div className="border-b border-border-light bg-white">
        <div className="max-w-[900px] mx-auto px-5 md:px-12 py-5">
          <p className="text-[14px] font-medium text-success">
            You have joined the {crew.destination_name} crew.
          </p>
        </div>
      </div>
    );
  }

  if (left) {
    return (
      <div className="border-b border-border-light bg-white">
        <div className="max-w-[900px] mx-auto px-5 md:px-12 py-5">
          <p className="text-[14px] text-text-muted">
            You have left the {crew.destination_name} crew.
          </p>
        </div>
      </div>
    );
  }

  // Logged-out visitor
  if (!currentUserId) {
    return (
      <div className="border-b border-border-light bg-white sticky top-0 z-20">
        <div className="max-w-[900px] mx-auto px-5 md:px-12 py-4 flex flex-wrap items-center justify-between gap-4">
          <p className="text-[14px] text-text-body">
            Create a free account to join this crew.
          </p>
          <Link
            href="/signup"
            className="inline-block px-5 h-[44px] rounded-[10px] bg-navy text-white text-[14px] font-semibold min-w-[140px] text-center leading-[44px] hover:bg-navy-hover transition-colors no-underline shrink-0"
          >
            Sign up free
          </Link>
        </div>
      </div>
    );
  }

  // Already a confirmed member: show a leave option
  if (memberStatus === "confirmed") {
    async function handleLeave() {
      setPending(true);
      setErr(null);
      const result = await leaveCrew(crew.slug);
      setPending(false);
      if (result.success) {
        setLeft(true);
      } else {
        setErr(result.error ?? "Something went wrong.");
      }
    }

    return (
      <div className="border-b border-border-light bg-white sticky top-0 z-20">
        <div className="max-w-[900px] mx-auto px-5 md:px-12 py-4 flex flex-wrap items-center justify-between gap-4">
          <div>
            <p className="text-[14px] font-medium text-success">
              You are in the {crew.destination_name} crew.
            </p>
            {err && (
              <p className="text-[13px] text-error mt-1">{err}</p>
            )}
          </div>
          <button
            type="button"
            onClick={handleLeave}
            disabled={pending}
            aria-label={`Leave the ${crew.destination_name} crew`}
            className="px-5 py-0 h-[44px] rounded-[10px] border border-border text-[14px] font-medium text-text-muted min-w-[100px] hover:border-error/40 hover:text-error transition-colors disabled:opacity-60 shrink-0"
          >
            {pending ? "Leaving..." : "Leave crew"}
          </button>
        </div>
      </div>
    );
  }

  // Discovery member or not a member: show join/confirm CTA
  async function handleJoin() {
    setPending(true);
    setErr(null);

    // If the user is in discovery (has availability windows), use the original
    // confirm flow. Otherwise use the direct join.
    const result =
      memberStatus === "discovery"
        ? await confirmCrewDestination(crew.slug)
        : await joinCrew(crew.slug);

    setPending(false);

    const success = "ok" in result ? result.ok : result.success;
    const error = result.error;

    if (success) {
      setDone(true);
    } else {
      setErr(error ?? "Something went wrong.");
    }
  }

  return (
    <div className="border-b border-border-light bg-white sticky top-0 z-20">
      <div className="max-w-[900px] mx-auto px-5 md:px-12 py-4 flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className="text-[14px] text-text-body">
            {memberStatus === "discovery" ? (
              <>
                Confirmed you are heading to{" "}
                <span className="font-semibold">{crew.destination_name}</span>?
              </>
            ) : (
              <>
                Interested in{" "}
                <span className="font-semibold">{crew.destination_name}</span>?
                Join the crew.
              </>
            )}
          </p>
          {err && (
            <p className="text-[13px] text-error mt-1">{err}</p>
          )}
        </div>
        <button
          type="button"
          onClick={handleJoin}
          disabled={pending}
          aria-label={
            memberStatus === "discovery"
              ? `Confirm I am going to ${crew.destination_name}`
              : `Join the ${crew.destination_name} crew`
          }
          className="px-5 py-0 h-[44px] rounded-[10px] bg-navy text-white text-[14px] font-semibold min-w-[140px] hover:bg-navy-hover transition-colors disabled:opacity-60 shrink-0"
        >
          {pending ? "Joining..." : "Join the crew"}
        </button>
      </div>
    </div>
  );
}

// ─── Member grid ──────────────────────────────────────────────────────────────

const INITIAL_VISIBLE = 20;

type GridItem =
  | { kind: "single"; member: CrewMemberRow }
  | { kind: "pair"; a: CrewMemberRow; b: CrewMemberRow };

function buildRows(
  members: CrewMemberRow[],
  linkedPairs: LinkedPair[]
): GridItem[] {
  // Index accepted pairs by user_id, only counting links where both parties are
  // members of this section (parameter `members`).
  const memberIds = new Set(members.map((m) => m.user_id));
  const partnerOf = new Map<string, string>();
  for (const p of linkedPairs) {
    if (!memberIds.has(p.a) || !memberIds.has(p.b)) continue;
    // If either user is already linked in this section, skip the extra edge
    // (each member is paired with at most one other, preserving 2-col layout).
    if (partnerOf.has(p.a) || partnerOf.has(p.b)) continue;
    partnerOf.set(p.a, p.b);
    partnerOf.set(p.b, p.a);
  }

  const byUser = new Map(members.map((m) => [m.user_id, m]));
  const used = new Set<string>();
  const rows: GridItem[] = [];

  for (const m of members) {
    if (used.has(m.user_id)) continue;
    const partnerId = partnerOf.get(m.user_id);
    if (partnerId && !used.has(partnerId) && byUser.has(partnerId)) {
      rows.push({ kind: "pair", a: m, b: byUser.get(partnerId)! });
      used.add(m.user_id);
      used.add(partnerId);
    } else {
      rows.push({ kind: "single", member: m });
      used.add(m.user_id);
    }
  }
  return rows;
}

function MemberCard({
  member,
  showConfirmedBadge,
  crewDestinationName,
}: {
  member: CrewMemberRow;
  showConfirmedBadge: boolean;
  crewDestinationName: string;
}) {
  if (!member.profile) return null;
  const { profile, window: win } = member;
  const badge = showConfirmedBadge
    ? win?.placed_company
      ? `Confirmed: working at ${win.placed_company}`
      : `Confirmed: heading to ${crewDestinationName}`
    : null;

  return (
    <Link
      href={`/candidates/${profile.id}`}
      className="block h-full border border-border-light rounded-[14px] p-4 hover:border-gold/40 hover:-translate-y-0.5 transition-all no-underline bg-white"
      aria-label={`View ${profile.first_name} ${profile.last_name}'s profile`}
    >
      <div className="flex items-center gap-3 mb-3">
        <div className="w-11 h-11 rounded-full overflow-hidden shrink-0">
          {profile.photo_url && isSupabaseUrl(profile.photo_url) ? (
            <Image
              src={profile.photo_url}
              alt={`${profile.first_name} ${profile.last_name}`}
              width={44}
              height={44}
              sizes="44px"
              className="w-full h-full object-cover"
            />
          ) : (
            <div
              className={`w-full h-full bg-gradient-to-br ${avatarGradient(profile.user_id)} flex items-center justify-center text-white font-serif font-semibold text-[15px]`}
              aria-hidden
            >
              {getInitials(profile.first_name, profile.last_name)}
            </div>
          )}
        </div>
        <div>
          <div className="font-semibold text-[14px] text-charcoal leading-snug">
            {profile.first_name} {profile.last_name}
          </div>
          {profile.experience_level && (
            <div className="text-[12px] text-text-muted">
              {experienceLabel(profile.experience_level)}
            </div>
          )}
        </div>
      </div>

      {(win?.positions ?? []).length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-2">
          {(win?.positions ?? []).slice(0, 2).map((r) => (
            <span
              key={r}
              className="px-2 py-0.5 bg-gold-pale text-gold rounded-[5px] text-[11px] font-medium"
            >
              {findLabelById(ROLES_TREE, r) ?? r}
            </span>
          ))}
        </div>
      )}

      {win && (
        <div className="text-[12px] text-text-muted mb-2">
          {formatDate(win.available_from)} to {formatDate(win.available_to)}
        </div>
      )}

      {profile.bio && (
        <p className="text-[12px] text-text-body leading-[1.5] line-clamp-2 mb-2">
          {profile.bio}
        </p>
      )}

      {badge && (
        <span className="inline-block mt-1 px-2.5 py-1 bg-[#EDFBF0] text-[#1C6E2E] rounded-[6px] text-[11px] font-medium">
          {badge}
        </span>
      )}
    </Link>
  );
}

function GoingTogetherChip() {
  return (
    <span
      className="inline-flex items-center gap-1.5 bg-white border border-gold/40 text-gold text-[11px] font-semibold px-2.5 py-1 rounded-full shadow-sm whitespace-nowrap"
      aria-label="Going together"
    >
      <svg
        width="10"
        height="10"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden
      >
        <path d="M10 13a5 5 0 0 0 7.07 0l3-3a5 5 0 0 0-7.07-7.07l-1 1" />
        <path d="M14 11a5 5 0 0 0-7.07 0l-3 3a5 5 0 0 0 7.07 7.07l1-1" />
      </svg>
      Going together
    </span>
  );
}

function MemberGrid({
  members,
  showConfirmedBadge,
  crewDestinationName,
  linkedPairs,
}: {
  members: CrewMemberRow[];
  showConfirmedBadge: boolean;
  crewDestinationName: string;
  linkedPairs: LinkedPair[];
}) {
  const [showAll, setShowAll] = useState(false);
  const visible = showAll ? members : members.slice(0, INITIAL_VISIBLE);
  const hasMore = members.length > INITIAL_VISIBLE && !showAll;

  const rows = useMemo(() => buildRows(visible, linkedPairs), [visible, linkedPairs]);

  return (
    <div>
      <ul className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
        {rows.map((row) => {
          if (row.kind === "single") {
            const m = row.member;
            if (!m.profile) return null;
            return (
              <li key={m.profile.id}>
                <MemberCard
                  member={m}
                  showConfirmedBadge={showConfirmedBadge}
                  crewDestinationName={crewDestinationName}
                />
              </li>
            );
          }
          // Pair: spans 2 columns on sm+; stacks with chip between on mobile.
          if (!row.a.profile || !row.b.profile) return null;
          return (
            <li
              key={`${row.a.profile.id}__${row.b.profile.id}`}
              className="sm:col-span-2"
            >
              <div className="flex flex-col sm:flex-row items-stretch gap-3">
                <div className="flex-1 min-w-0">
                  <MemberCard
                    member={row.a}
                    showConfirmedBadge={showConfirmedBadge}
                    crewDestinationName={crewDestinationName}
                  />
                </div>
                <div className="flex items-center justify-center shrink-0 sm:px-1">
                  <GoingTogetherChip />
                </div>
                <div className="flex-1 min-w-0">
                  <MemberCard
                    member={row.b}
                    showConfirmedBadge={showConfirmedBadge}
                    crewDestinationName={crewDestinationName}
                  />
                </div>
              </div>
            </li>
          );
        })}
      </ul>

      {hasMore && (
        <div className="mt-6 text-center">
          <button
            type="button"
            onClick={() => setShowAll(true)}
            className="px-6 py-0 h-[44px] rounded-[10px] border border-border text-[14px] font-medium text-charcoal hover:border-gold/40 hover:text-gold transition-colors"
          >
            Show {members.length - INITIAL_VISIBLE} more
          </button>
        </div>
      )}
    </div>
  );
}

// ─── JSON-LD ──────────────────────────────────────────────────────────────────

function JsonLd({
  crew,
  audienceSummary,
  childCounts,
}: {
  crew: CrewRow;
  audienceSummary: CrewAudienceSummary;
  childCounts: ChildCrewCount[];
}) {
  const dest = crew.destination_name;
  const season = crew.season_label;
  const faqs: Array<{ q: string; a: string }> = [];

  // Always: season dates
  faqs.push({
    q: `When is the ${dest} ${season}?`,
    a: `The ${dest} ${season} runs from ${formatDate(crew.date_from)} to ${formatDate(crew.date_to)}.`,
  });

  // Conditional: total count
  if (audienceSummary.total >= 5) {
    faqs.push({
      q:
        crew.season_type === "festivals"
          ? `Who is heading to ${dest} ${season}?`
          : `How many seasonaires are heading to ${dest} for ${season}?`,
      a: `${audienceSummary.total} seasonaire${audienceSummary.total !== 1 ? "s" : ""} in the crew${audienceSummary.confirmed > 0 ? `, ${audienceSummary.confirmed} confirmed` : ""}.`,
    });
  }

  // Conditional: popular roles (if >= 3 distinct roles across >= 5 windows)
  if (audienceSummary.roleCounts.length >= 3 && audienceSummary.total >= 5) {
    const top3 = audienceSummary.roleCounts
      .slice(0, 3)
      .map(({ value }) => findLabelById(ROLES_TREE, value) ?? value);
    faqs.push({
      q: `What roles are most common for seasonaires in ${dest}?`,
      a: `The most popular roles in the ${dest} ${season} crew are ${top3.join(", ")}.`,
    });
  }

  // Regional: child festivals/resorts module
  if (crew.kind === "regional" && childCounts.length >= 2) {
    const top5 = childCounts.slice(0, 5).map((c) => c.destination_name);
    faqs.push({
      q: `Which ${crew.season_type === "festivals" ? "festivals are in the crew" : "resorts are most popular"}?`,
      a: `Top destinations in the ${dest} ${season} crew: ${top5.join(", ")}.`,
    });
  }

  // Always: how to join
  faqs.push({
    q: `How do I join the ${dest} crew?`,
    a: `Create a free profile on PeakWave and add your availability for ${season}. You will automatically appear in the crew for ${dest}.`,
  });

  const webPage = {
    "@type": "WebPage",
    name: `${dest} ${season} Crew`,
    url: `https://peakwave.co/community/crew/${crew.slug}`,
    description: `Seasonaires heading to ${dest} for ${season}.`,
  };

  // Event node (formerly a second <script> emitted by the server page). Folded
  // into this @graph so the detail page ships a single JSON-LD block.
  const event = {
    "@type": "Event",
    name: `${dest} ${season} Crew`,
    description: `${audienceSummary.total} seasonaires heading to ${dest} for ${season}. Join the crew free on PeakWave.`,
    url: `https://peakwave.co/community/crew/${crew.slug}`,
    startDate: crew.date_from,
    endDate: crew.date_to,
    location: { "@type": "Place", name: dest },
    organizer: { "@type": "Organization", name: "PeakWave", url: "https://peakwave.co" },
    eventAttendanceMode: "https://schema.org/OfflineEventAttendanceMode",
    isAccessibleForFree: true,
  };

  const graph: unknown[] = [webPage, event];
  if (faqs.length >= 2) {
    graph.push({
      "@type": "FAQPage",
      mainEntity: faqs.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    });
  }

  const ld: unknown = {
    "@context": "https://schema.org",
    "@graph": graph,
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(ld) }}
    />
  );
}
